import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Tabs, Table, Button, Modal, Form, Input, InputNumber, Select, Checkbox, Space, Typography, Tag, Drawer, DatePicker, Descriptions, notification, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, FileTextOutlined, DeleteOutlined, PrinterOutlined } from '@ant-design/icons';
import moment from 'moment';

import { useAuth } from '../../authentication/AuthProvider';
import request from '../../lib/request';
import { useBooks, BooksSelector, BooksCaption } from '../../lib/books';

import { date as fmtDate, label } from '../../lib/format';
import { CountrySelect } from '../../components/CountrySelect';
const money = (v) => (v === null || v === undefined ? '' : Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const TYPES = [{ value: 'CUSTOMER', label: 'Customer' }, { value: 'SUPPLIER', label: 'Supplier' }, { value: 'BOTH', label: 'Customer and supplier' }];

/** Statement of one customer / supplier: the control-account lines with a running balance. */
function StatementDrawer({ row, side, onClose }) {
  const [range, setRange] = React.useState([moment().startOf('year'), moment()]);
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    if (!row) return setData(null);
    request
      .get(`/counterparties/${row.id}/statement`, { from: range[0].format('YYYY-MM-DD'), to: range[1].format('YYYY-MM-DD'), side })
      .then((r) => r.json())
      .then((r) => (r.status ? setData(r.data) : notification.error({ message: r.message })));
  }, [row, range, side]);
  return (
    <Drawer title={row ? `${row.code} ${row.name} - statement` : ''} open={!!row} onClose={onClose} width={860}>
      {data && (
        <div>
          <Space wrap style={{ marginBottom: 12 }} className="no-print">
            <DatePicker.RangePicker value={range} onChange={(v) => v && setRange(v)} allowClear={false} />
            <Button icon={<PrinterOutlined />} onClick={() => window.print()}>Print</Button>
          </Space>
          {data.note && <Typography.Paragraph type="warning">{data.note}</Typography.Paragraph>}
          {data.account && (
            <Descriptions size="small" column={3} style={{ marginBottom: 12 }}>
              <Descriptions.Item label="Account">{data.account.code} {data.account.name}</Descriptions.Item>
              <Descriptions.Item label="Opening">{money(data.opening)}</Descriptions.Item>
              <Descriptions.Item label={data.side === 'creditors' ? 'Owed to supplier' : 'Owed by customer'}><strong>{money(data.owed)}</strong></Descriptions.Item>
            </Descriptions>
          )}
          <Table
            size="small"
            rowKey="id"
            pagination={false}
            dataSource={data.rows}
            columns={[
              { title: 'Date', dataIndex: 'date', width: 100, render: (v) => fmtDate(v) },
              { title: 'Number', dataIndex: 'entry_number', width: 120, render: (v, r) => <Link to={`/journal?entry=${r.entry_id}`}>{v}</Link> },
              { title: 'Code', dataIndex: 'transaction_code', width: 70, render: (v) => (v ? <Tag>{v}</Tag> : '') },
              { title: 'Reference', dataIndex: 'reference', render: (v) => v || '-' },
              { title: 'Details', dataIndex: 'details', render: (v) => v || '-' },
              { title: 'Debit', dataIndex: 'debit', align: 'right', width: 100, render: (v) => (v ? money(v) : '') },
              { title: 'Credit', dataIndex: 'credit', align: 'right', width: 100, render: (v) => (v ? money(v) : '') },
              { title: 'Balance', dataIndex: 'balance', align: 'right', width: 110, render: (v) => <strong>{money(v)}</strong> },
            ]}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={7}><strong>Closing balance</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={7} align="right"><strong>{money(data.closing)}</strong></Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
        </div>
      )}
    </Drawer>
  );
}

/** Customers / Suppliers of one set of books (xlsx MENU "Accounting -> Customers / Suppliers"). */
export default function CounterpartiesPage() {
  const auth = useAuth();
  const [params] = useSearchParams();
  const canDelete = auth.user.role === 'SYSTEM_ADMIN' || auth.user.role === 'COMPANY_MANAGER';
  const { ledgerId, ledger } = useBooks();
  const [tab, setTab] = React.useState(params.get('type') === 'SUPPLIER' ? 'SUPPLIER' : 'CUSTOMER');
  const [rows, setRows] = React.useState([]);
  const [accounts, setAccounts] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [q, setQ] = React.useState('');
  const [editing, setEditing] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [statement, setStatement] = React.useState(null);
  const [form] = Form.useForm();

  const fetchRows = React.useCallback(() => {
    if (!ledgerId) return setRows([]);
    setLoading(true);
    request
      .get('/counterparties', { ledger_id: ledgerId, type: tab, ...(q ? { q } : {}) })
      .then((r) => r.json())
      .then((r) => (r.status ? setRows(r.data) : notification.error({ message: r.message })))
      .finally(() => setLoading(false));
  }, [ledgerId, tab, q]);
  React.useEffect(fetchRows, [fetchRows]);
  React.useEffect(() => {
    if (!ledgerId) return;
    request.get('/accounts', { ledger_id: ledgerId }).then((r) => r.json()).then((r) => r.status && setAccounts(r.data.filter((a) => !a.is_heading)));
  }, [ledgerId]);

  const openNew = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ type: tab, status: 'ACTIVE', country: 'CY' });
    setOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    form.setFieldsValue(r);
    setOpen(true);
  };
  const save = () =>
    form.validateFields().then((values) => {
      (editing ? request.put(`/counterparties/${editing.id}`, values) : request.post('/counterparties', { ...values, ledger_id: ledgerId }))
        .then((r) => r.json())
        .then((r) => {
          if (!r.status) return notification.error({ message: r.message });
          notification.success({ message: `${r.data.code} ${r.data.name} saved` });
          setOpen(false);
          fetchRows();
        });
    });
  const remove = (r) => request.delete(`/counterparties/${r.id}`).then((x) => x.json()).then((x) => (x.status ? fetchRows() : notification.error({ message: x.message })));

  const isSupplierTab = tab === 'SUPPLIER';
  const columns = [
    { title: 'Code', dataIndex: 'code', width: 90 },
    { title: 'Name', dataIndex: 'name', render: (v, r) => <Typography.Link onClick={() => setStatement(r)}>{v}</Typography.Link> },
    { title: 'Type', dataIndex: 'type', width: 100, render: (v) => (v === 'BOTH' ? <Tag>both</Tag> : '') },
    { title: 'VAT number', dataIndex: 'vat_number', width: 140, render: (v) => v || '-' },
    { title: 'Country', dataIndex: 'country', width: 80, render: (v) => v || '-' },
    { title: 'Contact', key: 'c', render: (_, r) => [r.contact_person, r.email, r.telephone].filter(Boolean).join(' - ') || '-' },
    { title: 'Terms', dataIndex: 'payment_terms_days', width: 80, render: (v) => (v !== null && v !== undefined ? `${v} d` : '-') },
    { title: isSupplierTab ? 'Owed to' : 'Owed by', dataIndex: isSupplierTab ? 'payable' : 'receivable', align: 'right', width: 110, render: (v) => <strong>{money(v)}</strong> },
    { title: 'Status', dataIndex: 'status', width: 90, render: (v) => <Tag color={v === 'ACTIVE' ? 'green' : 'default'}>{label(v)}</Tag> },
    {
      title: '',
      key: 'a',
      width: 120,
      render: (_, r) => (
        <Space size={4}>
          <Button size="small" icon={<FileTextOutlined />} title="Statement" onClick={() => setStatement(r)} />
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          {canDelete && (
            <Popconfirm title="Remove this entry? Only possible while nothing is posted to it." onConfirm={() => remove(r)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const table = <Table rowKey="id" size="small" columns={columns} dataSource={rows} loading={loading} pagination={{ pageSize: 25 }} locale={{ emptyText: ledgerId ? `No ${isSupplierTab ? 'suppliers' : 'customers'} yet` : 'Choose the books first' }} />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 16 }}>
        <div>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Customers / Suppliers
          </Typography.Title>
          <div><BooksCaption /></div>
          <Typography.Text type="secondary">The counterparties of these books. Pick them on transaction rows so statements, ageing and the VIES / OSS country split come from the register.</Typography.Text>
        </div>
        <Space wrap>
          <BooksSelector />
          <Input.Search placeholder="Search name, code, VAT no." allowClear style={{ width: 220 }} onSearch={setQ} />
          <Button type="primary" icon={<PlusOutlined />} onClick={openNew} disabled={!ledgerId}>
            New {isSupplierTab ? 'Supplier' : 'Customer'}
          </Button>
        </Space>
      </div>
      {ledger && !ledger.client_id && (
        <Typography.Paragraph type="secondary">The firm's own customers are its clients (see <Link to="/clients">Clients</Link> and the invoices); this register is mainly for the firm's suppliers.</Typography.Paragraph>
      )}
      <Tabs activeKey={tab} onChange={setTab} items={[{ key: 'CUSTOMER', label: 'Customers', children: table }, { key: 'SUPPLIER', label: 'Suppliers', children: table }]} />

      <Modal title={editing ? `Edit ${editing.code} ${editing.name}` : `New ${isSupplierTab ? 'supplier' : 'customer'}`} open={open} onCancel={() => setOpen(false)} onOk={save} width={720}>
        <Form form={form} layout="vertical">
          <Space style={{ display: 'flex' }} align="start">
            <Form.Item name="name" label="Name" rules={[{ required: true }]} style={{ width: 420 }}><Input maxLength={150} /></Form.Item>
            <Form.Item name="type" label="Type" rules={[{ required: true }]} style={{ width: 220 }}><Select options={TYPES} /></Form.Item>
          </Space>
          <Space style={{ display: 'flex' }} align="start">
            <Form.Item name="registration_number" label="Registration no." style={{ width: 200 }}><Input maxLength={60} /></Form.Item>
            <Form.Item name="vat_number" label="VAT number" style={{ width: 200 }} tooltip="EU format with the country prefix, e.g. CY10123456X"><Input maxLength={40} /></Form.Item>
            <Form.Item name="tax_id" label="Tax id (TIC)" style={{ width: 200 }}><Input maxLength={40} /></Form.Item>
          </Space>
          <Space style={{ display: 'flex' }} align="start">
            <Form.Item name="country" label="Country" style={{ width: 220 }}><CountrySelect valueType="code" /></Form.Item>
            <Form.Item name="contact_person" label="Contact person" style={{ width: 240 }}><Input maxLength={120} /></Form.Item>
            <Form.Item name="email" label="E-mail" style={{ width: 240 }}><Input maxLength={120} /></Form.Item>
          </Space>
          <Space style={{ display: 'flex' }} align="start">
            <Form.Item name="telephone" label="Telephone" style={{ width: 180 }}><Input maxLength={40} /></Form.Item>
            <Form.Item name="payment_terms_days" label="Payment terms (days)" style={{ width: 170 }}><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
            <Form.Item name="default_account_id" label="Default sales / purchase account" style={{ width: 300 }}><Select allowClear showSearch optionFilterProp="label" options={accounts.map((a) => ({ value: a.id, label: `${a.code} ${a.name}` }))} /></Form.Item>
          </Space>
          <Form.Item name="address" label="Address"><Input.TextArea rows={2} /></Form.Item>
          <Space>
            <Form.Item name="is_related_party" valuePropName="checked"><Checkbox>Related party</Checkbox></Form.Item>
            <Form.Item name="status" label="Status" style={{ width: 160 }}><Select options={[{ value: 'ACTIVE', label: 'Active' }, { value: 'INACTIVE', label: 'Inactive' }]} /></Form.Item>
          </Space>
          <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>

      <StatementDrawer row={statement} side={isSupplierTab ? 'creditors' : 'debtors'} onClose={() => setStatement(null)} />
    </div>
  );
}
